import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { useFormik } from "formik";
import * as Yup from "yup";
import { Alert, Button, Col, Row, Form, Spinner } from '@themesberg/react-bootstrap';
import axios from "axios";
import { API_URL, END_POINT_COMPANY } from "../const";
import { showSuccessToast } from "../utils/toast";
import EditFormLayout from "../components/EditFormLayout";
import EditFormActions from "../components/EditFormActions";

export const CompanyEditForm = ({ companyId, onSuccess, onCancel, onSubmittingChange }) => {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const saving = useRef(false);
  const token = sessionStorage.getItem('quotetoken')?.replace(/^"|"$/g, '');

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setCompany(null);
    setLoading(true);
    setLoadError("");
    axios.get(API_URL + END_POINT_COMPANY + "/" + companyId, {
      headers: { Accept: 'application/json', Authorization: 'Bearer ' + token },
      signal: controller.signal,
    }).then(({ data }) => {
      if (!active) return;
      if (!data.status || !data.data || String(data.data.id) !== String(companyId)) {
        setLoadError((!data.status && data.message) || 'Unable to load company details. Please try again.');
        return;
      }
      setCompany(data.data);
    }).catch(error => {
      if (active) setLoadError(error.response?.data?.message || 'Unable to load company details. Please try again.');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [companyId, token, reloadKey]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { name: company?.name || "", address: company?.address || "" },
    validationSchema: Yup.object({
      name: Yup.string().required("Company name is required"),
      address: Yup.string().required("Company address is required"),
    }),
    onSubmit: async (values, { setSubmitting, setStatus, setErrors }) => {
      if (saving.current) return;
      if (loading || loadError || !company || String(company.id) !== String(companyId)) {
        setSubmitting(false);
        return;
      }
      saving.current = true;
      setStatus(null);
      onSubmittingChange?.(true);
      let result;
      try {
        const response = await axios.put(API_URL + END_POINT_COMPANY + "/" + companyId, values, {
          headers: { Accept: 'application/json', Authorization: 'Bearer ' + token },
        });
        if (!response.data.status) {
          setStatus(response.data.message || 'Failed to update company.');
          return;
        }
        result = response.data;
      } catch (error) {
        const errors = error.response?.data?.errors;
        if (errors) {
          setErrors(Object.fromEntries(
            Object.entries(errors).map(([field, messages]) => [
              field, Array.isArray(messages) ? messages[0] : messages,
            ])
          ));
        }
        setStatus(error.response?.data?.message || 'Failed to update company.');
      } finally {
        saving.current = false;
        setSubmitting(false);
        onSubmittingChange?.(false);
      }
      if (result) onSuccess(result);
    },
  });

  if (loading || loadError || !company || String(company.id) !== String(companyId)) {
    return (
      <>
        {loading ? (
          <div role="status" className="d-flex align-items-center gap-2 py-4">
            <Spinner animation="border" size="sm" aria-hidden="true" />
            Loading company details...
          </div>
        ) : (
          <Alert variant="danger">
            {loadError || 'Unable to load company details. Please try again.'}
            <Button variant="link" type="button" onClick={() => setReloadKey(value => value + 1)}>Retry</Button>
          </Alert>
        )}
        {onCancel && (
          <div className="create-record-form__actions d-flex justify-content-end gap-2 mt-2">
            <Button variant="outline-secondary" type="button" onClick={onCancel}>Cancel</Button>
          </div>
        )}
      </>
    );
  }

  return (
    <Form noValidate onSubmit={formik.handleSubmit} aria-busy={formik.isSubmitting}>
      {formik.status && <Alert variant="danger">{formik.status}</Alert>}
      <Row>
        <Col md={6} className="mb-3">
          <Form.Group controlId="edit-company-name">
            <Form.Label>Company Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              autoFocus
              disabled={formik.isSubmitting}
              {...formik.getFieldProps("name")}
              isInvalid={formik.touched.name && formik.errors.name}
            />
            <Form.Control.Feedback type="invalid">{formik.errors.name}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6} className="mb-3">
          <Form.Group controlId="edit-company-address">
            <Form.Label>Company Address <span className="text-danger">*</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              disabled={formik.isSubmitting}
              {...formik.getFieldProps("address")}
              isInvalid={formik.touched.address && formik.errors.address}
            />
            <Form.Control.Feedback type="invalid">{formik.errors.address}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      {onCancel ? (
        <div className="create-record-form__actions d-flex justify-content-end gap-2 mt-2">
          <Button variant="outline-secondary" type="button" onClick={onCancel} disabled={formik.isSubmitting}>Cancel</Button>
          <Button variant="primary" type="submit" className="btn-quote-blue"
            disabled={!formik.dirty || formik.isSubmitting}>
            {formik.isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      ) : (
        <EditFormActions backTo="/company" isDirty={formik.dirty}
          saveDisabled={!formik.dirty} isSubmitting={formik.isSubmitting} />
      )}
    </Form>
  );
};

export const EditCompanyForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const handleSuccess = result => {
    showSuccessToast(result.message);
    navigate('/company');
  };

  return (
    <EditFormLayout title="Edit Company" subtitle="Update company name and address" backTo="/company">
      <CompanyEditForm key={id} companyId={id} onSuccess={handleSuccess} />
    </EditFormLayout>
  );
};
